import React from 'react';
import { Award, BookOpen, Users, Heart, Star, CheckCircle } from 'lucide-react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { psychologistData } from '../mock';

const AboutSection = () => {
  return (
    <section id="about" className="py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-5xl font-bold text-charcoal-grey mb-6">
            About Manish Pacher
          </h2>
          <p className="text-xl text-charcoal-grey max-w-3xl mx-auto leading-relaxed">
            A dedicated Clinical Psychologist with over 20 years of experience helping individuals, 
            couples, and families build stronger, healthier relationships through evidence-based therapeutic approaches.
          </p>
        </div>

        <div className="max-w-5xl mx-auto space-y-16">
          {/* Professional Experience */}
          <Card className="p-8 bg-white shadow-lg">
            <h3 className="text-2xl font-bold text-sage-green mb-6 flex items-center">
              <BookOpen className="mr-3 text-sage-green" size={24} />
              Professional Experience
            </h3>
            <div className="space-y-4">
              <div className="border-l-4 border-blue-200 pl-4">
                <h4 className="font-semibold text-gray-900">Consultant Psychologist</h4>
                <p className="text-gray-600">Bhaktivedanta Hospital</p>
              </div>
              <div className="border-l-4 border-green-200 pl-4">
                <h4 className="font-semibold text-gray-900">Psychiatry Ward Officer</h4>
                <p className="text-gray-600">P.V. Doshi Hospital</p>
              </div>
              <div className="border-l-4 border-blue-200 pl-4">
                <h4 className="font-semibold text-gray-900">Community Counseling</h4>
                <p className="text-gray-600">20+ years of dedicated service</p>
              </div>
            </div>
          </Card>

          {/* Education & Certifications */}
          <Card className="p-8 bg-white shadow-lg">
            <h3 className="text-2xl font-bold text-soft-blue mb-6 flex items-center">
              <Award className="mr-3 text-soft-blue" size={24} />
              Education & Certifications
            </h3>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <CheckCircle className="text-green-600 mt-1 flex-shrink-0" size={16} />
                <div>
                  <p className="font-semibold text-gray-900">MA Clinical Psychology</p>
                  <p className="text-sm text-gray-600">Delhi University</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <CheckCircle className="text-green-600 mt-1 flex-shrink-0" size={16} />
                <div>
                  <p className="font-semibold text-gray-900">Stanford University School of Medicine</p>
                  <p className="text-sm text-gray-600">Specialized Training</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <CheckCircle className="text-green-600 mt-1 flex-shrink-0" size={16} />
                <div>
                  <p className="font-semibold text-gray-900">CBT Certification</p>
                  <p className="text-sm text-gray-600">Under Albert Ellis Supervisor, USA</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <CheckCircle className="text-green-600 mt-1 flex-shrink-0" size={16} />
                <div>
                  <p className="font-semibold text-gray-900">PDG School Counselling</p>
                  <p className="text-sm text-gray-600">Symbiosis, Pune</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <CheckCircle className="text-green-600 mt-1 flex-shrink-0" size={16} />
                <div>
                  <p className="font-semibold text-gray-900">Bach Flower & Yoga Therapy</p>
                  <p className="text-sm text-gray-600">Holistic healing approaches</p>
                </div>
              </div>
            </div>
          </Card>

          {/* Specializations */}
          <Card className="p-8 bg-white shadow-lg">
            <h3 className="text-2xl font-bold text-lavender mb-6 flex items-center">
              <Heart className="mr-3 text-lavender" size={24} />
              Areas of Specialization
            </h3>
            <div className="grid gap-3">
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                <p className="text-gray-700">Child Counselling</p>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                <p className="text-gray-700">Youth Counselling</p>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                <p className="text-gray-700">Marriage/Couples Counselling</p>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                <p className="text-gray-700">Family Therapy</p>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                <p className="text-gray-700">CBT (Cognitive Behavioral Therapy)</p>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                <p className="text-gray-700">DBT (Dialectical Behavior Therapy)</p>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                <p className="text-gray-700">TA (Transactional Analysis)</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Philosophy & Approach */}
        <Card className="mt-16 p-12 bg-gradient-to-r from-blue-50 to-green-50 border-0">
          <div className="text-center max-w-4xl mx-auto">
            <h3 className="text-3xl font-bold text-soft-blue mb-6">My Approach to Healing</h3>
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <Badge className="bg-sage-green text-white px-4 py-2 text-sm">Evidence-Based</Badge>
              <Badge className="bg-soft-blue text-white px-4 py-2 text-sm">Compassionate Care</Badge>
              <Badge className="bg-lavender text-white px-4 py-2 text-sm">Holistic Approach</Badge>
              <Badge className="bg-deep-teal text-white px-4 py-2 text-sm">Individual-Centered</Badge>
            </div>
            <p className="text-lg text-gray-700 leading-relaxed">
              "I believe in the power of compassionate, evidence-based therapy to transform lives. My approach 
              combines years of clinical experience with genuine care for each individual's unique journey. 
              Whether working with couples, families, or individuals, I create a safe, non-judgmental space 
              where healing and growth can flourish."
            </p>
          </div>
        </Card>
      </div>
    </section>
  );
};

export default AboutSection;